import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { fetchClients, fetchClientById, deleteClient, clearError } from '../redux/slices/clientsSlice';
import { AppDispatch, RootState } from '../redux/store';
import Layout from '../components/Layout';
import ClientDetailDrawer from '../components/ClientDetailDrawer';
import ClientFormModal from '../components/ClientFormModal';
import PWAAccessModal from '../components/PWAAccessModal';
import { Client } from '../types';
import { useMediaQuery, useTheme, IconButton, Tooltip, Chip, Pagination, Box, Snackbar, Alert } from '@mui/material';
import { VpnKey as VpnKeyIcon, Add as AddIcon } from '@mui/icons-material';

const ClientsList: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const { clients, loading, pagination, currentClient, currentClientHistorique, currentClientAuth, error } = useSelector((state: RootState) => state.clients);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pwaModalOpen, setPwaModalOpen] = useState(false);
  const [selectedPWAClient, setSelectedPWAClient] = useState<{ id: number; name: string; hasAccess: boolean } | null>(null);
  const [page, setPage] = useState(1);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);

  useEffect(() => {
    dispatch(fetchClients({ page, limit: 20, search, type_client: typeFilter }));
  }, [dispatch, page, search, typeFilter]);

  // Recharger les clients au montage du composant
  useEffect(() => {
    dispatch(fetchClients({ page: 1, limit: 20 }));
  }, [dispatch]);

  // Afficher les erreurs
  useEffect(() => {
    if (error) {
      setShowErrorSnackbar(true);
    }
  }, [error]);

  const handleClientClick = (client: Client) => {
    if (isDesktop) {
      // Sur desktop: charger les détails et afficher dans le panneau latéral
      setSelectedClientId(client.id);
      dispatch(fetchClientById(client.id));
    } else {
      // Sur mobile/tablette: naviguer vers la page
      navigate(`/clients/${client.id}`);
    }
  };

  const handleCloseDrawer = () => {
    setSelectedClientId(null);
  };

  const handleEdit = (client: Client) => {
    navigate(`/clients/${client.id}/edit`);
  };

  const handleDelete = async (id: number) => {
    await dispatch(deleteClient(id));
    dispatch(fetchClients({ page: 1, limit: 20, search, type_client: typeFilter }));
  };

  const handleModalSuccess = () => {
    dispatch(fetchClients({ page: 1, limit: 20, search, type_client: typeFilter }));
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenPWAModal = (client: Client) => {
    setSelectedPWAClient({
      id: client.id,
      name: client.type_client === 'entreprise' ? client.raison_sociale || '' : `${client.prenom} ${client.nom}`,
      hasAccess: client.acces_espace_client || false
    });
    setPwaModalOpen(true);
  };

  const handleClosePWAModal = () => {
    setPwaModalOpen(false);
    setSelectedPWAClient(null);
  };

  const handlePWASuccess = () => {
    dispatch(fetchClients({ page: 1, limit: 20, search, type_client: typeFilter }));
  };

  const handleCloseErrorSnackbar = () => {
    setShowErrorSnackbar(false);
    dispatch(clearError());
  };

  return (
    <Layout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h1>Clients</h1>
        <button onClick={() => setIsModalOpen(true)} className="btn btn-primary">
          + Nouveau client
        </button>
      </div>

      <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', width: '100%' }}>
        {/* Table Section */}
        <div
          className="card"
          style={{
            flex: '1 1 auto',
            transition: 'all 0.3s ease',
            minWidth: 0
          }}
        >
          <div style={{ display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: '200px', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
            />

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
            >
              <option value="">Tous les types</option>
              <option value="particulier">Particulier</option>
              <option value="entreprise">Entreprise</option>
            </select>
          </div>

          {loading ? (
            <div className="loading">Chargement...</div>
          ) : !clients || clients.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
              Aucun client trouvé
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Téléphone</th>
                    {!selectedClientId && <th>N° Client</th>}
                    <th>Accès PWA</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(clients || []).map((client) => (
                    <tr
                      key={client.id}
                      onClick={() => handleClientClick(client)}
                      style={{
                        cursor: 'pointer',
                        backgroundColor: selectedClientId === client.id ? '#e3f2fd' : 'transparent',
                        transition: 'background-color 0.2s ease'
                      }}
                    >
                      <td>
                        {client.type_client === 'entreprise'
                          ? client.raison_sociale
                          : `${client.nom} ${client.prenom || ''}`}
                      </td>
                      <td>{client.telephone_principal || '-'}</td>
                      {!selectedClientId && <td>#{client.id}</td>}
                      <td>
                        {client.acces_espace_client ? (
                          <Chip label="Actif" color="success" size="small" />
                        ) : (
                          <Chip label="Inactif" color="default" size="small" />
                        )}
                      </td>
                      <td>
                        <Tooltip title={client.acces_espace_client ? "Voir identifiants PWA" : "Activer PWA"}>
                          <IconButton
                            size="small"
                            color={client.acces_espace_client ? "primary" : "default"}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenPWAModal(client);
                            }}
                          >
                            {client.acces_espace_client ? <VpnKeyIcon /> : <AddIcon />}
                          </IconButton>
                        </Tooltip>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination && pagination.totalPages > 1 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: 2 }}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
              <p style={{ color: '#666', margin: 0 }}>
                Page {pagination.page} sur {pagination.totalPages} ({pagination.total} client{pagination.total > 1 ? 's' : ''} au total)
              </p>
            </Box>
          )}
        </div>

        {/* Side Panel - Desktop only */}
        {isDesktop && selectedClientId && currentClient && (
          <ClientDetailDrawer
            open={true}
            client={currentClient}
            historique={currentClientHistorique}
            espaceClientAuth={currentClientAuth}
            onClose={handleCloseDrawer}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        )}
      </div>

      {/* Modal de création de client */}
      <ClientFormModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* Modal de gestion d'accès PWA */}
      <PWAAccessModal
        open={pwaModalOpen}
        onClose={handleClosePWAModal}
        clientId={selectedPWAClient?.id || null}
        clientName={selectedPWAClient?.name || ''}
        hasAccess={selectedPWAClient?.hasAccess || false}
        onSuccess={handlePWASuccess}
      />

      {/* Snackbar pour les erreurs */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseErrorSnackbar} severity="error" variant="filled" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default ClientsList;
